import { actions, definitions } from '@luxuryescapes/contract-svc-agent'
import getAllItemViewsByType from 'checkout/selectors/view/getAllItemViewsByType'
import { OFFER_TYPE_BED_BANK, OFFER_TYPE_TOUR_V2, OFFER_TYPE_CRUISE, OFFER_TYPE_TOUR, OFFER_TYPE_ALWAYS_ON } from 'constants/offer'
import { sum, unique } from 'lib/array/arrayUtils'
import { isTourV2Offer, isCarHireOffer, isLpcHotelOffer, isBedbankOffer, isExperienceOffer } from 'lib/offer/offerTypes'
import { AgentHubGetOffersCommissionBody } from '../agentHubCommission'

type ServerAgentAccount = definitions['userSimpleSchema'] | definitions['meSchema']
type ServerAgencyDetails = definitions['userWithAgencyResponse']
type ServerAgentEmailStatus = definitions['userStatusResponse']
type ServerAgentCommission = actions['calculateCartCommission']['responses']['200']['content']['application/json']['result']
type ServerOfferCommission = actions['calculateOffersCommission']['responses']['200']['content']['application/json']['result'][number]
interface CalculateCartCommissionBodyItem {
  offerId: string
  type: string
  price: number
  location: Array<string>
  vendor: string
  transactionKey: string
  mx?: string
}

interface CalculateCartCommissionBody {
  cartId: string;
  items: Array<CalculateCartCommissionBodyItem>;
  total: number;
  transactionKey: string;
}

function sumViewItemTotal(itemView: App.Checkout.AnyItemView): number {
  const itemPrice = itemView.totals.price
  const itemTotalPrice = itemView.item.itemType === 'tourV2' ? itemView.item?.total ?? itemPrice : itemPrice
  return itemTotalPrice + (itemView.totals.surcharge ?? 0) + (itemView.totals.otherFees?.extraGuestSurcharge ?? 0)
}
function sumViewItemsTotal(items: Array<App.Checkout.AnyItemView>): number {
  return sum(items, (i) => sumViewItemTotal(i))
}

export function agentHubCheckEmailStatusMapper(response: ServerAgentEmailStatus): App.AgentHubAgentStatus {
  return {
    status: response.status ?? '',
    agentId: response.agent_id ?? '',
    action: response.action,
    ...(response.email && { email: response.email }),
    ...(response.firstName && { firstName: response.firstName }),
    ...(response.surname && { surname: response.surname }),
    ...(response.phone && { phone: response.phone }),
    ...(response.phone_prefix && { phonePrefix: response.phone_prefix }),
  }
}

export function agentHubAccountMapper(response: ServerAgentAccount): App.AgentHubAccount {
  return {
    agentId: response.id,
    name: response.name || '',
    phone: response.phone || '',
    email: response.email || '',
    affiliation: response.affiliation,
    agencyId: response.agencyId || '',
    agencyName: response.agencyName || '',
    status: response.status as any,
    agencyLogoId: response.agencyLogoId,
    agentTier: response.tier || '',
    hasAgencyIdentifier: ('hasAgencyIdentifier' in response) ? response.hasAgencyIdentifier : false,
    agentHasPayoutAccountSetup: ('hp' in response) ? response.hp : false,
  }
}

export function agentHubAgencyMapper(response: ServerAgencyDetails): App.AgentHubAgency {
  return {
    id: response.id,
    name: response.name || '',
    agencyIdentifier: response.businessCodeValue,
    agencyIdentifierType: response.businessCodeType,
    address: response.address,
    city: response.city,
    state: response.state,
    postcode: response.postcode,
    country: response.country,
    affiliation: response.affiliation,
    brand: response.brand,
  }
}

const getCommissionItems = (state: App.State): Array<CalculateCartCommissionBodyItem> => {
  const resultItems: Array<CalculateCartCommissionBodyItem> = []
  const { accommodationItemsView, tourV2ExperienceItemsView, experienceItemsView, transferItemsView, carHireItemsView } = getAllItemViewsByType(state).data
  const allOptionalExperiences = tourV2ExperienceItemsView?.data || []

  if (experienceItemsView.data.length > 0) {
    for (const item of experienceItemsView.data) {
      for (const ticket of item.ticketViews) {
        resultItems.push({
          offerId: item.id,
          type: 'experience',
          price: ticket.item.total ?? item.totals.price / (item.ticketViews.length ?? 1),
          location: [item.location || ''],
          vendor: '',
          transactionKey: ticket.item.transactionKey,
        })
      }
    }
  }

  for (const itemView of transferItemsView.data || []) {
    resultItems.push({
      offerId: itemView.item.experienceId,
      type: 'transfer',
      price: itemView.item.transfer.option?.price || 0,
      location: [],
      vendor: '',
      transactionKey: itemView.item.transactionKey,
    })
  }

  for (const itemView of carHireItemsView.data || []) {
    resultItems.push({
      offerId: itemView.item.offerId,
      type: 'car_hire',
      price: itemView.item.price,
      location: [],
      vendor: '',
      transactionKey: itemView.item.transactionKey,
    })
  }

  if (accommodationItemsView.data.length > 0) {
    for (const item of accommodationItemsView.data) {
      if (item.offerType === OFFER_TYPE_TOUR) {
        const locations = unique((item?.offer?.locations || []).concat(item?.offer?.location || []))

        for (const itemView of item.itemViews) {
          resultItems.push({
            offerId: item.offerId,
            type: item.offer?.productType || item.offerType,
            price: sumViewItemTotal(itemView),
            location: locations,
            vendor: item.offer?.vendorName || '',
            transactionKey: itemView.item.transactionKey,
            ...(itemView.pkg?.mx && { mx: itemView.pkg?.mx }),
          })
        }

        continue
      }

      if (item.offerType === OFFER_TYPE_BED_BANK) {
        const locations = [
          item?.offer?.property?.address.city || '',
          item?.offer?.property?.address.stateProvinceName || '',
          item.offer?.property?.address.countryName || '']
          .filter(Boolean)

        for (const itemView of item.itemViews) {
          resultItems.push({
            type: item.offer?.productType || '',
            offerId: item.offerId,
            price: sumViewItemTotal(itemView),
            location: locations,
            vendor: '',
            transactionKey: itemView.item.transactionKey,
            mx: itemView.rate?.mx,
          })
        }

        continue
      }

      if (item.offerType === OFFER_TYPE_TOUR_V2) {
        const tourV2StartLocation = () => {
          const variations = item.itemViews.map((x) => x.variation)
          if (variations.length === 0) return ['']
          const variation = variations[0]
          return [variation?.startLocation || '']
        }

        const currentTourOptionalExperiences = allOptionalExperiences.filter((oe) => oe.item.offerId === item.offerId)

        let leahProductType = item.offer?.productType || ''

        const newTourProductTypes = ['deluxe_tour', 'premium_tour', 'ultra_lux_tour', 'signature_series_tour']
        if (newTourProductTypes.includes(leahProductType)) {
          leahProductType = 'direct_tour'
        }

        resultItems.push({
          type: leahProductType || '',
          offerId: item.offerId,
          price: sumViewItemsTotal(item.itemViews),
          location: tourV2StartLocation(),
          vendor: '',
          transactionKey: item.itemViews[0].item.transactionKey,
        })

        for (const optionalExperience of currentTourOptionalExperiences) {
          resultItems.push({
            type: leahProductType,
            offerId: optionalExperience.packageId,
            price: optionalExperience.item.total || optionalExperience.item.purchasableOption.fullPrice,
            location: tourV2StartLocation(),
            vendor: '',
            transactionKey: item.itemViews[0].item.transactionKey,
          })
        }

        continue
      }

      if (item.offerType === OFFER_TYPE_CRUISE) {
        for (const itemView of item.itemViews) {
          resultItems.push({
            type: item.offer?.productType || '',
            offerId: item.offerId,
            price: sumViewItemTotal(itemView),
            location: [item.location],
            vendor: '',
            transactionKey: itemView.item.transactionKey,
          })
        }

        continue
      }

      const locations = unique((item?.offer?.locations || []).concat(item?.offer?.location || []))

      if (item.offerType === OFFER_TYPE_ALWAYS_ON) {
        for (const itemView of item.itemViews) {
          resultItems.push({
            offerId: item.offerId,
            type: item.offer?.productType || item.offerType,
            price: sumViewItemTotal(itemView),
            location: locations,
            vendor: item.offer?.vendorName || '',
            transactionKey: itemView.item.transactionKey,
            ...(itemView.pkg?.roomRate?.mx && { mx: itemView.pkg?.roomRate?.mx }),
          })
        }

        continue
      }

      for (const itemView of item.itemViews) {
        resultItems.push({
          offerId: item.offerId,
          type: item.offer?.productType || item.offerType,
          price: sumViewItemTotal(itemView),
          location: locations,
          vendor: item.offer?.vendorName || '',
          transactionKey: itemView.item.transactionKey,
          ...(itemView.pkg?.mx && { mx: itemView.pkg?.mx }),
        })
      }
    }
  }

  return resultItems
}

export const stateToCommissionOrder = (state: App.State): CalculateCartCommissionBody => {
  const items = getCommissionItems(state)
  return {
    items,
    cartId: state.checkout.cart.cartId,
    total: sum(items, (i) => i.price || 0),
    transactionKey: state.checkout.cart.transactionKey,
  }
}

export const agentHubCommissionMap = (serverCommission: ServerAgentCommission): App.AgentHubCommission => {
  return {
    promoCode: serverCommission.promoCode,
    itemsCommission: Object.fromEntries(
      Object.entries(serverCommission.itemsCommission).map(([key, value]) => [
        key,
        {
          commissionPercentage: value.commissionPercentage,
          itemDiscount: value.itemDiscount,
        },
      ]),
    ),
  }
}

export const mapOfferCommission = (serverOfferCommission: ServerOfferCommission): App.AgentHubOfferCommission => {
  return {
    offerId: serverOfferCommission.offerId,
    commissionPercentage: serverOfferCommission.commission,
    packages: serverOfferCommission.packages.map((pkg) => ({
      packageId: pkg.packageId,
      commissionPercentage: pkg.commission,
    })),
    minBaseCommission: serverOfferCommission.minBaseCommission,
    maxBaseCommission: serverOfferCommission.maxBaseCommission,
  }
}

export const mapOfferToAgentHubCommissionPayload = (offer: App.ExperienceOffer | App.Offer | App.BedbankOffer | App.CarHireOffer, offerRates: Record<string, Array<App.BedbankRate>> = {}): AgentHubGetOffersCommissionBody => {
  let productType = offer.productType
  const newTourProductTypes = ['deluxe_tour', 'premium_tour', 'ultra_lux_tour', 'signature_series_tour']
  if (newTourProductTypes.includes(productType)) {
    productType = 'direct_tour'
  } else if (!isCarHireOffer(offer) && isExperienceOffer(offer) && offer.productType !== 'transfer') {
    productType = 'experience'
  }

  let vendorName: string = ''
  if (isCarHireOffer(offer)) {
    vendorName = offer.vendor.name
  } else if (isBedbankOffer(offer)) {
    vendorName = ''
  } else if (isExperienceOffer(offer)) {
    vendorName = offer.vendor?.name || ''
  } else {
    vendorName = offer.vendorName || ''
  }

  let price = 0
  if (isCarHireOffer(offer)) {
    price = offer.price
  } else if (isBedbankOffer(offer)) {
    price = offer.packages[0].sell?.value ?? 0
  } else if (isExperienceOffer(offer)) {
    price = offer.price
  } else {
    price = offer.lowestPricePackage?.price ?? 0
  }

  const location: Array<string> = []

  if (isCarHireOffer(offer)) {
    location.push(offer.pickUp.name, offer.dropOff.name)
  } else if (isBedbankOffer(offer)) {
    location.push(offer.property.address.city)
  } else if (isTourV2Offer(offer)) {
    const tourV2Offer = offer as App.Tours.TourV2Offer
    const variation = Object.keys(tourV2Offer.variations)[0]
    location.push(tourV2Offer.variations[variation]?.startLocation ?? '')
  } else if (isExperienceOffer(offer)) {
    location.push(offer.location.name)
  } else {
    location.push(offer.location || '')
  }

  return {
    offerId: offer.id,
    location,
    price,
    type: productType,
    vendor: vendorName,
    packages: getAgentHubPackageMap(offer as App.Offer, offerRates || {}),
  }
}

export const getAgentHubPackageMap = (offer: App.Offer, offerRates: Record<string, Array<App.BedbankRate>>): AgentHubGetOffersCommissionBody['packages'] => {
  if (isBedbankOffer(offer)) {
    const offerRatesArray = Object.keys(offerRates)
    if (!offerRates || offerRatesArray.length === 0) {
      return []
    }

    return offerRates[offerRatesArray[0]].map(rate => {
      return {
        packageId: rate.id,
        mx: rate.mx || '',
      }
    })
  }

  if (!offer?.packages) {
    return []
  }

  if (isLpcHotelOffer(offer)) {
    return offer.packages.filter(pkg => pkg.roomRate?.mx).map(pkg => {
      return {
        packageId: pkg.id,
        mx: pkg.roomRate!.mx || '',
      }
    })
  }

  return offer.packages.filter(pkg => pkg.mx).map(pkg => ({
    packageId: pkg.id,
    mx: pkg.mx || '',
  }))
}
